import type { GeoClusterPoint, GeoContratoPoint, Criticidade } from '@/hooks/useGeoIntelData';

// ── Criticidade visual tokens ──

export const CRIT_COLORS: Record<Criticidade, string> = {
  critico: '#DC2626',
  alerta: '#E5A100',
  ok: '#16A34A',
  sem_dados: '#94A3B8',
};

export const CRIT_LABELS: Record<Criticidade, string> = {
  critico: 'Crítico',
  alerta: 'Alerta',
  ok: 'OK',
  sem_dados: 'Sem dados',
};

// ── GeoJSON builders ──

export function buildClustersGeoJSON(clusters: GeoClusterPoint[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: clusters
      .filter(c => c.has_valid_coordinates)
      .map(c => ({
        type: 'Feature' as const,
        properties: {
          id: c.id,
          name: c.name,
          color: c.color,
          criticidade: c.criticidade,
          critColor: CRIT_COLORS[c.criticidade],
          total_alertas: c.total_alertas,
          total_desvios: c.total_desvios,
          contratos_count: c.contratos.length,
          label: c.name.charAt(0).toUpperCase(),
        },
        geometry: {
          type: 'Point' as const,
          coordinates: c.coordinates,
        },
      })),
  };
}

export function buildContratosGeoJSON(contratos: GeoContratoPoint[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: contratos
      .filter(c => c.has_valid_coordinates)
      .map(c => ({
        type: 'Feature' as const,
        properties: {
          id: c.id,
          nome: c.nome,
          status: c.status,
          cluster_id: c.cluster_id,
          cluster_name: c.cluster_name,
          cluster_color: c.cluster_color,
          criticidade: c.criticidade,
          critColor: CRIT_COLORS[c.criticidade],
          spi: c.spi,
          cpi: c.cpi,
          percentual_fisico: c.percentual_fisico,
          alertas_abertos: c.alertas_abertos,
          desvios_abertos: c.desvios_abertos,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: c.coordinates,
        },
      })),
  };
}

export function buildClusterGeometriesGeoJSON(clusters: GeoClusterPoint[], allClusters: any[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  allClusters.forEach(rawCluster => {
    // rawCluster comes from useGeoIntelData, but geometry comes from a separate source
    // We need the geometry from the cluster data that includes it
  });

  return {
    type: 'FeatureCollection',
    features,
  };
}

// ── Bounds calculation ──

export function calculateBounds(
  clusters: GeoClusterPoint[],
  contratos: GeoContratoPoint[]
): [number, number, number, number] | null {
  const coords = [
    ...clusters.filter(c => c.has_valid_coordinates).map(c => c.coordinates),
    ...contratos.filter(c => c.has_valid_coordinates).map(c => c.coordinates),
  ];
  if (coords.length === 0) return null;

  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  coords.forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  });
  return [minLng, minLat, maxLng, maxLat];
}
